import { cloneElement, forwardRef, useCallback, useMemo, useRef } from 'react';
import { defaultRangeExtractor, useVirtual } from 'react-virtual';
import cx from 'classnames';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { useDesignTokens } from '../../theme';
import { findNextIndex } from '../../util/findIndex';
import { setRef } from '../../util/ref-util';
import { useDensity } from '../Density';
import { getListTokens } from './__internal__';
import { ListItem } from './ListItem/';
import './List.css';
import cn from './List.css.json';

/**
 * The VirtualListDesign component is a building block, with list virtualization,
 * for creating other components. Specifically,
 * it is for building lists that don’t contain interactive elements.
 * For example, it is used by the Combo Box component’s drop-down list.
 *
 * This component lacks major features and accessibility support. Consumers who need to
 * create standalone lists or who need to create list items that contain interactive
 * elements such as buttons should use the List component instead.
 *
 * <b>Note</b> this list does not take ListSeparator element, instead use showSeparator
 * prop at {@link ListItem} to enabled the separator.
 *
 * @param {VirtualListDesignProps} props the VirtualListDesignProps properties
 * @returns the VirtualListDesign element
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const VirtualListDesign = /*#__PURE__*/forwardRef(function VirtualListDesign(props, ref) {
  const ulRef = useRef(null);
  const parentRef = useRef(null);
  const itemHeight = Math.round(props.itemHeight ?? 30);
  const rangeRef = useRef({
    start: 0,
    end: 0
  });
  // FireFox most notably max out at 298,261 with the fixed items' height. So
  // making 295000 the max for all browsers.
  const count = Math.min(props.count ?? 0, 295000);
  const itemVirtualizer = useVirtual({
    size: count,
    parentRef,
    // use whole number due to using translateY, which may make some separators look fuzzy
    estimateSize: useCallback(() => itemHeight, [itemHeight]),
    overscan: 3,
    rangeExtractor: useCallback(range => {
      rangeRef.current = range;
      return defaultRangeExtractor(range);
    }, [])
  });
  const getItemProps = rowIndex => count < rowIndex || rowIndex > -1 ? props.renderItem?.({
    index: rowIndex
  })?.props : undefined;
  const isItemDisabled = rowIndex => !!getItemProps(rowIndex)?.disabled;
  const getItemText = rowIndex => getItemProps(rowIndex)?.text ?? '';
  const range = rangeRef.current.end - rangeRef.current.start;
  const pageCount = range === 0 ? 10 : range;
  const handleKeyDown = useVirtualListKeyDownHandler({
    length: count,
    pageCount,
    scrollToIndex: itemVirtualizer.scrollToIndex,
    isItemDisabled,
    getItemText,
    selectedIndex: props.selectedIndex,
    setFocusedIndex: props.setFocusedIndex,
    onSelectFocusedItem: props.onSelectFocusedItem
  });
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const tokenStyle = useMemo(() => getListTokens(dt, density, props.theme, props.font, props.variant, props.whiteSpace), [dt, density, props.theme, props.font, props.variant, props.whiteSpace]);
  const vsStyle = useMemo(() => {
    return {
      position: 'relative',
      height: `${itemVirtualizer.totalSize}px`,
      width: '100%',
      maxHeight: 'initial'
    };
  }, [itemVirtualizer]);
  if (props.virtualizerRef) props.virtualizerRef.current = itemVirtualizer;
  if (props.pageSizeRef) props.pageSizeRef.current = pageCount;
  return /*#__PURE__*/_jsx("div", {
    ref: parentRef,
    style: {
      height: props.style?.height ?? `200px`,
      width: props.style?.width ?? `400px`,
      maxHeight: props.style?.maxHeight,
      overflow: 'auto'
    },
    children: /*#__PURE__*/_jsx("ul", {
      ref: ul => {
        ulRef.current = ul;
        setRef(ref, ul);
      },
      className: cx(props.className, !!props.theme && cn.themeOverride, cn.list),
      style: {
        ...tokenStyle,
        ...props.style,
        ...vsStyle
      },
      role: props.role ?? 'listbox',
      "aria-label": props.ariaLabel,
      tabIndex: props.tabIndex ?? -1,
      "aria-labelledby": Array.isArray(props.ariaLabelledBy) ? props.ariaLabelledBy.length ? props.ariaLabelledBy.join(' ') : undefined : props.ariaLabelledBy,
      "aria-describedby": Array.isArray(props.ariaDescribedBy) ? props.ariaDescribedBy.length ? props.ariaDescribedBy.join(' ') : undefined : props.ariaDescribedBy,
      "data-testid": props.testId,
      onKeyDown: handleKeyDown,
      id: props.id,
      children: itemVirtualizer.virtualItems.map(virtualItem => {
        const index = virtualItem.index;
        const item = props.renderItem({
          index
        }) ?? /*#__PURE__*/_jsx(ListItem, {
          text: '',
          focused: false,
          selected: false,
          index: index
        }, `null-${index}`);
        return /*#__PURE__*/cloneElement(item, {
          className: cn.virtualItem,
          style: {
            height: `${virtualItem.size}px`,
            transform: `translateY(${virtualItem.start}px)`
          },
          'aria-posinset': index + 1,
          'aria-setsize': count
        });
      })
    })
  });
});
const useVirtualListKeyDownHandler = ({
  length,
  pageCount,
  scrollToIndex,
  isItemDisabled,
  getItemText,
  selectedIndex,
  setFocusedIndex,
  onSelectFocusedItem,
  doLooping
}) => {
  const earconError = useEarcon({
    source: EarconName.Error
  });
  return useCallback(e => {
    if (length <= 0) {
      return;
    }
    const maxIndex = length - 1;
    const getPrevFocusedIndex = prevFocusedIndex => prevFocusedIndex ?? selectedIndex;
    switch (e.key) {
      case 'ArrowDown':
        setFocusedIndex?.(prevFocusedIndex => {
          const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? -1;
          const index = prevIndex < length - 1 ? prevIndex + 1 : length - 1;
          scrollToIndex?.(index);
          return index;
        });
        break;
      case 'ArrowUp':
        setFocusedIndex?.(prevFocusedIndex => {
          const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? length;
          const index = prevIndex > 0 ? prevIndex - 1 : 0;
          scrollToIndex?.(index);
          return index;
        });
        break;
      case 'PageDown':
        {
          setFocusedIndex?.(prevFocusedIndex => {
            const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? -1;
            const index = Math.min(prevIndex + pageCount, maxIndex);
            scrollToIndex?.(index);
            return index;
          });
          break;
        }
      case 'PageUp':
        {
          setFocusedIndex?.(prevFocusedIndex => {
            const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? 0;
            const index = Math.max(prevIndex - pageCount, 0);
            scrollToIndex?.(index);
            return index;
          });
        }
        break;
      case 'End':
        {
          const index = length - 1;
          scrollToIndex?.(index);
          setFocusedIndex?.(index);
        }
        break;
      case 'Home':
        {
          const index = 0;
          scrollToIndex?.(index);
          setFocusedIndex?.(index);
        }
        break;
      case ' ':
      case 'Enter':
        onSelectFocusedItem?.();
        break;
      default:
        if (e.key.trim().length === 1) {
          setFocusedIndex?.(prevFocusedIndex => {
            const prevIndex = getPrevFocusedIndex(prevFocusedIndex) ?? -1;
            const key = e.key.toLowerCase();
            const index = findNextIndex({
              length,
              start: prevIndex + 1,
              isValidIndex: index => !isItemDisabled?.(index) && !!getItemText(index)?.toLowerCase().startsWith(key),
              wrap: doLooping ?? false
            });
            if (index !== undefined) scrollToIndex?.(index);else earconError.play();
            return index ?? prevIndex;
          });
          e.stopPropagation();
        }
    }
    e.preventDefault();
  }, [length, selectedIndex, setFocusedIndex, onSelectFocusedItem, scrollToIndex, pageCount, doLooping, earconError, isItemDisabled, getItemText]);
};
//# sourceMappingURL=VirtualListDesign.js.map